import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Scale, ArrowRight, FileText } from "lucide-react";
import type { Page } from "@shared/schema";

const LEGAL_ORDER = [
  "privacy",
  "terms",
  "subscription-terms",
  "cookie-policy",
  "eula",
  "acceptable-use",
  "data-retention",
  "accessibility",
  "copyright-notice",
  "advertiser-terms",
  "editorial-policy",
];

function sortLegalPages(pages: Page[]): Page[] {
  return [...pages].sort((a, b) => {
    const ai = LEGAL_ORDER.indexOf(a.slug);
    const bi = LEGAL_ORDER.indexOf(b.slug);
    if (ai === -1 && bi === -1) return a.title.localeCompare(b.title);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export default function LegalHubPage() {
  const { data: pages = [], isLoading } = useQuery<Page[]>({
    queryKey: ["/api/pages", "legal"],
    queryFn: async () => {
      const res = await fetch("/api/pages?type=legal");
      if (!res.ok) throw new Error("Failed to load legal pages");
      return res.json();
    },
    staleTime: 60_000,
  });

  const sorted = sortLegalPages(pages);

  return (
    <>
      <Helmet>
        <title>Legal Information | Future Manufacturing</title>
        <meta
          name="description"
          content="Privacy policy, terms of service, cookie policy, EULA, and all other legal documents for the Future Manufacturing platform."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Legal" }]} />
        </div>

        <main className="flex-1">
          {/* Hero */}
          <section className="border-b bg-muted/30 py-14">
            <div className="mx-auto max-w-7xl px-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Scale className="h-5 w-5 text-primary" />
                </div>
                <Badge variant="secondary">Legal Documents</Badge>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                Legal Information
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl">
                All our legal documents in one place — from privacy and cookies to editorial standards and advertiser terms. We aim to be transparent about how we operate.
              </p>
            </div>
          </section>

          {/* Document listing */}
          <section className="py-14">
            <div className="mx-auto max-w-7xl px-4">
              {isLoading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-36 rounded-xl" />
                  ))}
                </div>
              ) : sorted.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>Legal documents are being prepared. Check back shortly.</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sorted.map((page) => (
                    <Link
                      key={page.id}
                      href={`/page/${page.slug}`}
                      data-testid={`card-legal-${page.slug}`}
                    >
                      <div className="group h-full rounded-xl border bg-card p-6 hover:shadow-md transition-all hover:border-primary/40 cursor-pointer flex flex-col gap-3">
                        <div className="flex-1">
                          <h2 className="font-semibold text-base mb-1 group-hover:text-primary transition-colors">
                            {page.title}
                          </h2>
                          {page.metaDescription && (
                            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                              {page.metaDescription}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          {page.updatedAt && (
                            <span className="text-xs text-muted-foreground">
                              Updated {format(new Date(page.updatedAt), "d MMM yyyy")}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity ml-auto">
                            Read <ArrowRight className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Contact CTA */}
          <section className="py-10 border-t bg-muted/20">
            <div className="mx-auto max-w-7xl px-4 text-center">
              <p className="text-sm text-muted-foreground">
                Questions about our legal documents?{" "}
                <a href="mailto:legal@futuremfg.com" className="underline hover:text-foreground">
                  legal@futuremfg.com
                </a>
              </p>
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
